/**
 * 应用根组件 + 路由。
 *
 * 所有正式路由嵌套在 /:lang(zh|en|ja|ko) 下（语言源 = URL 前缀）。
 * LangSync 同步 i18next + <html lang> + localStorage。
 *
 * 路由（均在 /:lang 下）：
 * - /:lang/                                落地页
 * - /:lang/pricing                         定价
 * - /:lang/plaza  /:lang/plaza/:id         社区广场（/plaza/:id 重定向到广场列表，详情页已移除）
 * - /:lang/workbench                       工作台
 *   - generate / plaza / generations/:id/waiting|result / history / billing
 *   （分享到广场改为 HistoryPage 内弹窗 PublishModal，不再有独立路由；
 *    billing 为路由态弹窗——导航带 state.background 时背景页保持挂载，见 AppRoutes）
 *
 * 兼容旧扁平 URL：/、/pricing、/plaza、/plaza/:id（→ 广场列表）、/upload、/workbench/...
 * 一律重定向到带语言前缀的等价路径（语言由 detectLang 决定）。
 *
 * 未知路径（顶层 `*` 与 /:lang 下 `*`）渲染 NotFoundPage：服务端（Worker）
 * 对不在路由表内的路径返回 404 + noindex，客户端渲染 404 UI。
 */

import { lazy, Suspense, useMemo, type ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  type Location,
} from "react-router-dom";
import i18n from "./i18n";
import LandingPage from "./LandingPage";
import WorkbenchLayout from "./components/WorkbenchLayout";
import LangSync from "./components/LangSync";
import PublicLayout from "./components/PublicLayout";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { GaPageViews, GaSignUp } from "./components/Analytics";
import PricingPage from "./pages/PricingPage";
import PlazaPage from "./pages/PlazaPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AupPage from "./pages/AupPage";
import FaqPage from "./pages/FaqPage";
import NotFoundPage from "./pages/NotFoundPage";
import { detectLang, localizedPath } from "./lib/lang";

// 懒加载：结果页（3D 预览）+ 博客前台（按需加载，降低主包体积）
// M6: 管理后台已迁至独立应用 apps/admin（admin.tsuvi.com），主站不再包含 /admin
const ResultPage = lazy(() => import("./pages/ResultPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));

// 工作台页面懒加载：这些页面静态引用 lib/convert（three 导出器）等重依赖，
// 打进主包会让落地页首屏 modulepreload 整套 three chunk（~1.2MB 无用 JS）。
// 懒加载后入口只含公开 SEO 页 + 组件库；工作台首访多一次小 chunk 请求，
// 由 LazyRoute 的 Suspense 占位兜底。PlazaPage 因公开 /plaza 是 SEO 页保持静态
// （其 three 相关依赖均为 lazy/动态导入，不影响主包）。
const UploadPage = lazy(() => import("./pages/UploadPage"));
const ImageGenPage = lazy(() => import("./pages/ImageGenPage"));
const WaitingPage = lazy(() => import("./pages/WaitingPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));

// 内容页（guides / use-cases）懒加载：它们静态引入 react-markdown + remark-gfm
// （micromark/mdast/unified 全家桶，约 200KB），留在入口会拖慢所有页面的首屏
// JS。与博客（已懒加载）共享同一 markdown chunk；正文本就由 SPA 渲染，
// prerender 只做 meta，懒加载不影响 SEO 抓取。
const GuidesIndexPage = lazy(() => import("./pages/GuidesIndexPage"));
const GuideArticlePage = lazy(() => import("./pages/GuideArticlePage"));
const UseCasesIndexPage = lazy(() => import("./pages/UseCasesIndexPage"));
const UseCasePage = lazy(() => import("./pages/UseCasePage"));

function ResultPageFallback() {
  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p>{i18n.t("common.loadingViewer")}</p>
    </div>
  );
}

/**
 * 懒加载页面的统一包裹：Suspense（加载占位）+ 路由级错误边界。
 * 边界主要兜底发版后旧 chunk URL 失效的 dynamic import 失败，
 * 避免冒泡到根边界把整站打成全屏错误页。
 */
function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

/**
 * 旧扁平 URL 重定向到带语言前缀的等价路径。
 * 用当前 location.pathname 拼前缀，保留 query/hash。
 */
function RedirectToLocalized() {
  const location = useLocation();
  // detectLang 读 localStorage + navigator.language，结果在一次挂载内稳定，memoize 避免每次渲染重复读取
  const lang = useMemo(() => detectLang(), []);
  const target = localizedPath(location.pathname, lang) + location.search + location.hash;
  return <Navigate to={target} replace />;
}

/**
 * 旧作品详情页（/:lang/plaza/:id、/:lang/workbench/plaza/:id）已移除。
 * 命中这些路径时重定向到对应的广场列表，避免外链落到兜底页。
 * 用绝对路径规避相对 `to` 在不同路由层级下的解析歧义。
 */
function RedirectPlazaDetail() {
  const { lang } = useParams();
  const { pathname } = useLocation();
  const fallbackLang = useMemo(() => detectLang(), []);
  const inWorkbench = pathname.includes("/workbench/plaza");
  const target = inWorkbench
    ? `/${lang ?? fallbackLang}/workbench/plaza`
    : `/${lang ?? fallbackLang}/plaza`;
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <>
      {/* GA4：路由 page_view + 新注册事件（组件内部自守卫，未配置 VITE_GA_ID 时为 no-op） */}
      <GaPageViews />
      <GaSignUp />
      {/* 路由导航回顶（后退与同页切语言除外，见组件注释） */}
      <ScrollToTop />
      <AppRoutes />
    </>
  );
}

/**
 * 路由树 + 「我的订阅」路由态弹窗。
 *
 * 弹窗路由（/workbench/billing）导航时携带 state.background（见 WorkbenchLayout
 * 账号菜单）：主路由树改按背景 location 匹配 → 背景工作台页保持挂载（不卸载、
 * 不整页刷新），弹窗层（fixed 遮罩）叠加其上，交互对齐 Clerk 的 modal。
 * 直接深链/刷新该 URL（无 background state）时，由主树内的 billing 路由兜底
 * （弹窗覆盖在空工作台壳上，关闭时 BillingPage 的 closeModal 回生成页）。
 */
function AppRoutes() {
  const location = useLocation();
  const background = (location.state as { background?: Location } | null)
    ?.background;
  return (
    <>
      <Routes location={background ?? location}>
      {/* 根 → 检测语言后跳 /:lang（默认 en，见 SEO_CONTENT_PLAN.md §4.4） */}
      <Route path="/" element={<Navigate to={`/${detectLang()}`} replace />} />

      {/* 旧扁平 URL 容错重定向：加语言前缀。
          tsuvi.com 虽是新域名，但品牌首发（PH/HN/Reddit）时仍可能误用扁平链接，
          保留此重定向成本极低且避免 404；canonical 指向带前缀版本，无重复内容风险。 */}
      <Route path="/pricing" element={<RedirectToLocalized />} />
      <Route path="/plaza" element={<RedirectToLocalized />} />
      {/* 详情页已移除：旧扁平 /plaza/:id 链接重定向到广场列表 */}
      <Route path="/plaza/:id" element={<Navigate to="/plaza" replace />} />
      <Route path="/upload" element={<RedirectToLocalized />} />
      <Route path="/workbench/*" element={<RedirectToLocalized />} />
      <Route path="/terms" element={<RedirectToLocalized />} />
      <Route path="/privacy" element={<RedirectToLocalized />} />
      <Route path="/aup" element={<RedirectToLocalized />} />
      <Route path="/guides" element={<RedirectToLocalized />} />
      <Route path="/guides/:slug" element={<RedirectToLocalized />} />
      <Route path="/use-cases" element={<RedirectToLocalized />} />
      <Route path="/use-cases/:slug" element={<RedirectToLocalized />} />
      <Route path="/faq" element={<RedirectToLocalized />} />

      {/* 所有正式路由嵌套在 /:lang 下 */}
      <Route path="/:lang" element={<LangSync />}>
        <Route index element={<LandingPage />} />
        <Route path="pricing" element={<PricingPage />} />

        {/* 法律文档（仅英文：非 en 由 LegalPage 内的 EnglishOnlyRoute 重定向到 /en） */}
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="aup" element={<AupPage />} />

        {/* 广场（公开浏览；详情页已移除，旧 /plaza/:id 链接重定向到广场列表）。
            与 pricing/faq 等公开页一致套 PublicLayout（navbar + 限宽容器），
            工作台内（/workbench/plaza）仍由 WorkbenchLayout 提供 shell。 */}
        <Route
          path="plaza"
          element={
            <PublicLayout>
              <main className="plaza-public">
                <PlazaPage />
              </main>
            </PublicLayout>
          }
        />
        <Route path="plaza/:id" element={<RedirectPlazaDetail />} />

        {/* 内容板块（P1：仅英文，非 en 由 EnglishOnlyRoute 重定向到 /en）。
            懒加载：markdown 渲染栈不进主包（见顶部 import 注释） */}
        <Route
          path="guides"
          element={
            <LazyRoute>
              <GuidesIndexPage />
            </LazyRoute>
          }
        />
        <Route
          path="guides/:slug"
          element={
            <LazyRoute>
              <GuideArticlePage />
            </LazyRoute>
          }
        />
        <Route
          path="use-cases"
          element={
            <LazyRoute>
              <UseCasesIndexPage />
            </LazyRoute>
          }
        />
        <Route
          path="use-cases/:slug"
          element={
            <LazyRoute>
              <UseCasePage />
            </LazyRoute>
          }
        />
        {/* FAQ 四语都提供（i18n 驱动） */}
        <Route path="faq" element={<FaqPage />} />

        {/* 工作台 */}
        <Route
          path="workbench"
          element={
            <WorkbenchLayout>
              <Outlet />
            </WorkbenchLayout>
          }
        >
          <Route index element={<Navigate to="generate" replace />} />
          <Route
            path="generate"
            element={
              <LazyRoute>
                <UploadPage />
              </LazyRoute>
            }
          />
          <Route
            path="image-gen"
            element={
              <LazyRoute>
                <ImageGenPage />
              </LazyRoute>
            }
          />
          <Route path="plaza" element={<PlazaPage />} />
          <Route path="plaza/:id" element={<RedirectPlazaDetail />} />
          <Route
            path="generations/:id/waiting"
            element={
              <LazyRoute>
                <WaitingPage />
              </LazyRoute>
            }
          />
          <Route
            path="history"
            element={
              <LazyRoute>
                <HistoryPage />
              </LazyRoute>
            }
          />
          <Route
            path="billing"
            element={
              <LazyRoute>
                <BillingPage />
              </LazyRoute>
            }
          />
          {/* 旧路径兼容：account → billing */}
          <Route path="account" element={<Navigate to="../billing" replace />} />
          <Route
            path="generations/:id/result"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={<ResultPageFallback />}>
                  <ResultPage />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        </Route>

        {/* 工作台内旧路径兼容：/workbench/upload → generate（在 /:lang/workbench 下） */}
        <Route
          path="workbench/upload"
          element={<Navigate to="../workbench/generate" replace />}
        />
        {/* /:lang/upload 顶层兼容 */}
        <Route path="upload" element={<Navigate to="./workbench/generate" replace />} />

        {/* /:lang 下的未知子路径 → 404 页（服务端已返回 404 + noindex；
            不再重定向首页，避免 soft-404 信号并保留出错上下文） */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* M6: 管理后台已迁独立域名 admin(-staging).tsuvi.com，
          /admin 路径由 Worker 侧中间件 301，主站路由表不再包含 */}

      {/* 博客前台（英文独占，公开） */}
      <Route
        path="/blog"
        element={
          <LazyRoute>
            <BlogIndexPage />
          </LazyRoute>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <LazyRoute>
            <BlogArticlePage />
          </LazyRoute>
        }
      />

      {/* 未匹配 → 404 页（服务端未知路径同样返回 404 + noindex） */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* 弹窗层：带背景 state 导航到 billing 时叠加渲染（当前路径非 billing
          则不匹配任何路由，什么都不渲染）；无 background 的直接访问由上方
          主树内的 billing 路由兜底，不会走到这里 */}
      {background && (
        <Routes>
          <Route
            path="/:lang/workbench/billing"
            element={
              <LazyRoute>
                <BillingPage />
              </LazyRoute>
            }
          />
        </Routes>
      )}
    </>
  );
}

/** 通用页面加载占位 */
function PageFallback() {
  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
    </div>
  );
}
